/**
 * Pure helpers of the book UI (kept out of the component files for fast refresh).
 */
import { ROUTES } from '@/config/routes';
import type { BookAccess, BookVisibleState, Inline, TocEntry } from '@/services/api/bookContract';

/** The cover, copied from the public site; not under public/book/, which would shadow the /book route. */
export const BOOK_COVER = { small: '/book-cover/cover-360.webp', large: '/book-cover/cover-540.webp', width: 540, height: 764 } as const;

/** The book's text language when the edition does not say (D9: Montenegrin). */
export const DEFAULT_BOOK_LANG = 'cnr';

/** `lang` for book text (chapter blocks, chapter and part titles), as opposed to the UI language. */
export function bookLang(state: Pick<BookVisibleState, 'edition'>): string {
  return state.edition?.lang || DEFAULT_BOOK_LANG;
}

export const MIN_ZOOM = 1;
export const MAX_ZOOM = 4;

export function clampZoom(scale: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, scale));
}

/** Plain text of inline nodes (for labels); decorations are skipped. */
export function inlineText(nodes: readonly Inline[] | undefined): string {
  return (nodes ?? [])
    .map((node) => {
      switch (node.t) {
        case 'text':
          return node.v;
        case 'br':
          return ' ';
        case 'span':
          return node.deco ? '' : inlineText(node.c);
        case 'em':
        case 'strong':
        case 'ext':
          return inlineText(node.c);
        case 'link':
        case 'noteref':
          return inlineText(node.c);
        default:
          return '';
      }
    })
    .join('');
}

export interface TocGroup {
  entry: TocEntry;
  children: TocEntry[];
}

/**
 * The book's own TOC: entries without a level (spine items the EPUB's TOC does not list, such as
 * the cover or the title page) are left out; they stay reachable through previous/next. Level-1
 * entries start a group; deeper entries belong to the group above them.
 */
export function groupToc(toc: readonly TocEntry[]): TocGroup[] {
  const groups: TocGroup[] = [];
  for (const entry of toc) {
    if (entry.level === null) continue;
    const current = groups[groups.length - 1];
    if (entry.level > 1 && current) current.children.push(entry);
    else groups.push({ entry, children: [] });
  }
  return groups;
}

export function isChapterOpen(entry: TocEntry, access: BookAccess): boolean {
  return access === 'full' || entry.preview;
}

/** Where "Počni/Nastavi čitanje" goes: the saved place if its chapter is still listed, else the first entry. */
export function readingTarget(state: BookVisibleState): { to: string; resume: boolean } | null {
  const progress = state.progress;
  if (progress && state.toc.some((entry) => entry.id === progress.chapterId)) {
    return { to: ROUTES.bookChapter(progress.chapterId, progress.blockId), resume: true };
  }
  const first = state.toc[0];
  return first ? { to: ROUTES.bookChapter(first.id), resume: false } : null;
}

